import type { SiteLocale } from "@graphql/types/graphql";
import { getPublicLocale, type Locale, type PublicLocale } from "consts/locale";

const CMS_LOCALE_BY_APP_LOCALE: Record<Locale, SiteLocale> = {
  "nl-BE": "nl_BE" as SiteLocale,
  "fr-BE": "fr_BE" as SiteLocale,
  "en-BE": "en_BE" as SiteLocale,
  "fr-LU": "fr_LU" as SiteLocale,
  "de-LU": "de_LU" as SiteLocale,
  "en-LU": "en_LU" as SiteLocale,
};

const APP_LOCALE_BY_CMS_LOCALE = Object.fromEntries(
  Object.entries(CMS_LOCALE_BY_APP_LOCALE).map(([app, cms]) => [cms, app]),
) as Record<SiteLocale, Locale>;

export const getCmsLocale = (locale: Locale): SiteLocale => {
  return CMS_LOCALE_BY_APP_LOCALE[locale] ?? ("nl_BE" as SiteLocale);
};

/** Converts a raw DatoCMS locale string (e.g. "nl_BE", "fr_LU") to the app Locale type. */
export const getAppLocale = (cmsLocale: string): Locale => {
  return APP_LOCALE_BY_CMS_LOCALE[cmsLocale as SiteLocale] ?? "nl-BE";
};

/**
 * CVWarehouse API segment (Json1_17/…) — must follow app language, not full BCP-47 app locale.
 * Comparing to "fr" / "en" alone was wrong: callers pass Locale values like "fr-BE", so we always
 * fell through to the Dutch feed.
 */
export const getCVWarehouseLocale = (locale: Locale | undefined): string => {
  if (!locale) {
    return "nl-BE";
  }

  const publicLocale = getPublicLocale(locale);

  switch (publicLocale) {
    case "fr":
      return "fr-FR";
    case "en":
      return "en-BE";
    default:
      return "nl-BE";
  }
};

// This function returns the URL for the international page based on the locale and type, we can remove this once the footer is dynamically ajustable via DatoCMS
export const getInternationalUrl = (
  locale: Locale,
  type: "belgium" | "netherlands" | "luxembourg",
): string => {
  const publicLocale = getPublicLocale(locale);

  if (type === "belgium") {
    return publicLocale === "nl" || publicLocale === "fr"
      ? "https://www.piagroup.company/en?utm_source=pia.be&utm_medium=referral&utm_campaign=Homepage"
      : "#";
  }

  if (type === "netherlands") {
    return publicLocale === "nl" || publicLocale === "fr"
      ? "https://www.pianederland.nl?utm_source=pia.be&utm_medium=referral&utm_campaign=Homepage"
      : "#";
  }

  if (type === "luxembourg") {
    if (publicLocale === "nl") {
      return "https://neoviaq.com/nl/?utm_source=pia.be&utm_medium=referral&utm_campaign=Homepage";
    }
    if (publicLocale === "fr") {
      return "https://neoviaq.com/?utm_source=pia.be&utm_medium=referral&utm_campaign=Homepage";
    }
    return "#";
  }

  return "#";
};
